/**
 * f12.1: the review screen -- the FS sheet -- after `review.html`.
 *
 * Route `/cycles/:cycleId/review?units=<id>,<id>`. One cycle presents as
 * either meeting (Asset Management, or Banking & Industry) through the unit
 * selection, which lives in the URL (Q-f12.1-3). Without `units` the first
 * visible group is selected and written to the URL.
 *
 * The page head keeps an empty action slot: f12.3 (Lock) and f13.1 (Export)
 * fill it. Validation warnings (f11.6) are shown below the table and never
 * block a save.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, TriangleAlert } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import type { Review, ReviewRow } from '@/api/client';
import { ValidationWarningList, useCycleValidationWarnings } from '@/features/forecast/validation';
import { ExportCycleCsvButton } from '@/features/cycle-export/ExportCycleCsvButton';
import { CycleSubmitAction } from '@/features/cycle-submission/SubmitCycleButton';
import { LiveIndicator } from '@/live/LiveIndicator';
import { useLiveCycle, useLiveFallback } from '@/live/useLiveCycle';
import { RecordUnavailable } from '@/ui/RecordUnavailable';
import '@/styles/review.css';

import { FrozenBanner } from '../freeze/FrozenBanner';
import { CycleLockAction } from '../freeze/LockCycleDialog';
import { ReviewAttributesDialog } from './ReviewAttributesDialog';
import { ReviewStatCards } from './ReviewStatCards';
import { ReviewTable } from './ReviewTable';
import { ReviewUnitFilter } from './ReviewUnitFilter';
import { useReview, useReviewUnits } from './useReview';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** "Jul 2026" -> "July": the closing month the legacy comment column was named after. */
function closingMonthName(label: string): string {
  const abbreviation = label.slice(0, 3).toLowerCase();
  return MONTH_NAMES.find((name) => name.toLowerCase().startsWith(abbreviation)) ?? label;
}

function parseUnits(raw: string | null): string[] {
  return (raw ?? '').split(',').map((id) => id.trim()).filter(Boolean);
}

/** The row as the cache holds it now (live pushes included), for the open modal. */
function latestRow(review: Review, row: ReviewRow): ReviewRow | undefined {
  const unit = review.units.find((u) => u.business_unit.id === row.business_unit_id);
  return unit?.rows.find((r) => r.client.id === row.client.id);
}

function WarningsCard({ cycleId, unitIds }: { cycleId: string; unitIds: readonly string[] }) {
  const [open, setOpen] = useState(false);
  const warnings = useCycleValidationWarnings(cycleId);
  const lines = (warnings.data?.lines ?? []).filter((line) => unitIds.includes(line.business_unit_id));
  if (!lines.length) return null;
  return (
    <div className="card rv-warnings">
      <button type="button" className="card-head rv-warnings-head" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
        <TriangleAlert aria-hidden="true" />
        <h3>Check input</h3>
        <span className="hint">{lines.length} forecast lines carry validation warnings · they never block a save</span>
      </button>
      {open && (
        <ul className="rv-warning-lines">
          {lines.map((line) => (
            <li key={line.line_id}>
              <div className="rv-warning-line-head">
                <b>{line.account_name ?? line.element_code}</b>
                <span className="mono">{line.element_code}</span>
                <span className="hint">{line.business_unit_name}</span>
              </div>
              <ValidationWarningList warnings={line.validation_warnings} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ReviewPage() {
  const { cycleId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const units = useReviewUnits(cycleId);
  const selected = useMemo(() => parseUnits(params.get('units')), [params]);
  const [risk, setRisk] = useState('');
  const [editing, setEditing] = useState<ReviewRow | null>(null);

  const firstGroup = units.data?.groups[0];
  useEffect(() => {
    if (selected.length === 0 && firstGroup && firstGroup.units.length > 0) {
      setParams({ units: firstGroup.units.map((u) => u.id).join(',') }, { replace: true });
    }
  }, [selected.length, firstGroup, setParams]);

  // f12.2: one live stream for the current selection; a unit-filter change
  // aborts it and opens one for the new selection. Polling is only the fallback.
  const live = useLiveCycle({ cycleId, view: 'review', unitIds: selected });
  const fallback = useLiveFallback(live.status);
  const review = useReview(cycleId, selected, { poll: fallback });

  const setUnits = (next: string[]) => setParams({ units: next.join(',') });

  // f13.2: once offered, the submit action stays mounted so its outcome toast
  // survives the refetch that removes the offer (the cycle is then Submitted).
  const submitOffered = useRef(false);
  if (review.data?.cycle.can_submit) submitOffered.current = true;

  if (units.isError || review.isError) {
    return (
      <RecordUnavailable
        error={units.error ?? review.error}
        backTo={`/cycles/${encodeURIComponent(cycleId)}`}
        backLabel="Back to the cycle"
        onRetry={() => void (units.isError ? units.refetch() : review.refetch())}
      />
    );
  }

  if (units.data && units.data.groups.length === 0) {
    return (
      <div className="empty">
        <h3>Nothing to review</h3>
        <p>This cycle has no Business Unit in your scope.</p>
      </div>
    );
  }

  const data = review.data;
  if (!units.data || !data) return <div className="hint">Loading the review…</div>;

  const count = data.units.length;
  return (
    <>
      <div className="page-head">
        <div>
          <nav className="header-crumb mb-4" aria-label="Breadcrumb">
            <Link to="/cycles">Cycles</Link> <span>/</span>{' '}
            <Link to={`/cycles/${encodeURIComponent(cycleId)}`}>{data.cycle.code}</Link> <span>/</span> <b>Review</b>
          </nav>
          <h1 className="page-title">Review · {data.cycle.label}</h1>
          <div className="page-sub">
            Cycle <span className="mono">{data.cycle.code}</span> · commitment by client across the selected units
          </div>
        </div>
        {/* f13.1 (Export), f12.3 (Lock) and f13.2 (Submit) fill this slot. */}
        <div className="page-actions" data-slot="review-actions">
          <ExportCycleCsvButton cycleId={cycleId} stateCode={data.cycle.state} />
          {data.cycle.can_lock && <CycleLockAction cycleId={cycleId} />}
          {submitOffered.current && <CycleSubmitAction cycleId={cycleId} />}
        </div>
      </div>

      <FrozenBanner
        stateCode={data.cycle.state}
        snapshot={data.cycle.current_snapshot}
        reopened={data.cycle.reopened}
      />

      <LiveIndicator live={live} />

      {!data.cycle.writable && (
        <div className="alert alert-neutral mb-4" role="status">
          <div>
            <div className="a-title">Cycle {data.cycle.code} is {data.cycle.state_name}</div>
            <div className="a-body">The review attributes are read-only in this state.</div>
          </div>
        </div>
      )}

      <ReviewUnitFilter units={units.data} selected={selected} onChange={setUnits} risk={risk} onRisk={setRisk} />

      <ReviewStatCards review={data} />

      <div className="card">
        <div className="card-head">
          <h3>Commitment by client</h3>
          <span className="hint">
            FS sheet layout · {count} Business {count === 1 ? 'Unit' : 'Units'} selected
            {risk && ' · Filtered view — totals include all clients'}
          </span>
        </div>
        <ReviewTable review={data} cycleId={cycleId} riskFilter={risk} onEdit={setEditing} />
      </div>

      <WarningsCard cycleId={cycleId} unitIds={selected} />

      {editing && (
        <ReviewAttributesDialog
          cycleId={cycleId}
          cycleCode={data.cycle.code}
          closingMonthName={closingMonthName(data.cycle.closing_month_label)}
          row={editing}
          latest={latestRow(data, editing)}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}
